import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  data: {
    exams: [],
    meta: {},
  },
  workerExamsList: [],
  currentExam: null,
  examDetails: null,
  isLoading: false,
  // True while a fill_exam (test save) request is in flight. The Report Preview
  // tab uses this to show the "data is getting saved" notice until the save
  // actually finishes on the backend.
  saving: false,
  // True once a save has completed but the report hasn't been regenerated since.
  // Forces Report Preview to regenerate instead of opening a stale report,
  // because Redux job_run isn't refreshed on a plain save. Reset by editExam
  // (which triggers the regeneration).
  reportDirty: false,
  selectedTab: 1,
  jobs: [],
  companies: [],
  companiesLoading: false,
  companiesError: null,
  consultantCompanies: [], // New state for consultant companies
  consultantCompaniesLoading: false,
  consultantCompaniesError: null,
  selectedJobs: [],
  workerJobs: [],
  workerJobsLoading: false,
  functionReport: null,
  functionReportLoading: false,
  functionReportError: null,
  examJobs: [],
  examJobsLoading: false,
  examJobsError: null,
  comments: [],
  commentsLoading: false,
  commentsError: null,
  reportJobLoading: false, // New: Tracks loading state for report job status
  reportJobError: null,
};

export const getWorkerExams = createAsyncThunk(
  "exams/getWorkerExams",
  async ({ userId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/worker_exams?user_id=${userId}&account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch worker exams");
    }
  }
);

export const createNewExam = createAsyncThunk(
  "exams/createNewExam",
  async ({ exam, selectedAccount, header_test_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams?account_id=${selectedAccount}&header_test_id=${header_test_id}`;
    try {
      const response = await axios.post(url, exam, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const editExam = createAsyncThunk(
  "exams/editExam",
  async ({ exam, exam_id, selectedAccount, header_test_id, showLoading = true }, { rejectWithValue }) => {
    const bearerToken = getToken();
    let url = `${apiBaseUrl}/exams/${exam_id}?account_id=${selectedAccount}`;
    if (header_test_id !== null) {
      url += `&header_test_id=${header_test_id}`;
    }
    try {
      const response = await axios.put(url, exam, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteExam = createAsyncThunk(
  "exams/deleteExam",
  async ({ exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}?account_id=${selectedAccount}`;
    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Exam deleted successfully");
      return { exam_id };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteExamTest = createAsyncThunk(
  "exams/deleteExamTest",
  async ({ exam_test_id, exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exam_tests/${exam_test_id}?account_id=${selectedAccount}&exam_id=${exam_id}`;
    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return { exam_test_id, exam_id };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete exam test");
    }
  }
);

export const getExam = createAsyncThunk(
  "exams/getExam",
  async ({ exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// The report viewer's data. Split from getExam because report_data is often
// megabytes and nothing else that reads an exam wants it. It lands in
// state.currentExam like getExam's response, so the viewer reads the same keys.
export const getExamReportData = createAsyncThunk(
  "exams/getExamReportData",
  async ({ exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/report_data?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchExamSummary = createAsyncThunk(
  "exams/fetchExamSummary",
  async ({ exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/summary?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch exam summary");
    }
  }
);

export const getConsultantCompanies = createAsyncThunk(
  "exams/getConsultantCompanies",
  async (_, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/companies/workerfit_consultant_companies_api`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch consultant companies");
    }
  }
);

export const deleteWorkerJob = createAsyncThunk(
  "exams/deleteWorkerJob",
  async ({ jobId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/functions/${jobId}?account_id=${selectedAccount}`;
    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Job deleted successfully");
      return { jobId };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete worker job");
    }
  }
);
export const getCompanies = createAsyncThunk(
  "exams/getCompanies",
  async (consultantCompanyId, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/companies/workerfit_company_api?consultant_company_id=${consultantCompanyId}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch companies");
    }
  }
);

export const fetchFunctionReport = createAsyncThunk(
  "exams/fetchFunctionReport",
  async ({ function_id, company_id, user_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/companies/fetch_function_report?function_id=${function_id}&company_id=${company_id}&user_id=${user_id}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch function report");
    }
  }
);

export const fetchCompanyJobs = createAsyncThunk(
  "exams/fetchCompanyJobs",
  async ({ companyId, consultantCompanyId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/companies/fetch_company_jobs?company_id=${companyId}&consultant_company_id=${consultantCompanyId}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      const jobsWithCompany = response.data.map((job) => ({
        ...job,
        company_id: companyId,
      }));
      return jobsWithCompany;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch company jobs");
    }
  }
);

export const fetchCompanyJobsForEdit = createAsyncThunk(
  "exams/fetchCompanyJobsForEdit",
  async ({ companyId, consultantCompanyId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/companies/fetch_company_jobs?company_id=${companyId}&consultant_company_id=${consultantCompanyId}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      const jobsWithCompany = response.data.map((job) => ({
        ...job,
        company_id: companyId,
      }));
      return jobsWithCompany;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch company jobs");
    }
  }
);

export const workerJobs = createAsyncThunk(
  "exams/workerJobs",
  async ({ subject_id, job_id, selectedAccount, exam_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/functions/worker_jobs?subject_id=${subject_id}&account_id=${selectedAccount}&job_id=${job_id}&exam_id=${exam_id}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch worker jobs");
    }
  }
);

export const shareExamSummary = createAsyncThunk(
  "exams/shareExamSummary",
  async ({ examId, emails, comment, accountId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exam_summary_shares`;
    try {
      const response = await axios.post(
        url,
        { exam_id: examId, emails, comment, account_id: accountId },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error sharing exam summary");
    }
  }
);


export const copyExam = createAsyncThunk(
  "exams/copyExam",
  async ({ exam_id, exam, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/copy_exam?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, { exam }, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Exam copied successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to copy exam");
    }
  }
);

export const getExamDetails = createAsyncThunk(
  "exams/getExamDetails",
  async ({ exam_id, selectedAccount, showLoading = true }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/public_exam?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch exam details");
    }
  }
);

export const fillExam = createAsyncThunk(
  "exams/fillExam",
  async ({ exam_id, formData, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/fill_exam?account_id=${selectedAccount}`;
    try {
      const response = await axios.patch(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error submitting the form");
    }
  }
);

export const getAllExams = createAsyncThunk(
  "exams/getAllExams",
  async (params, { rejectWithValue }) => {
    const bearerToken = getToken();
    const { selectedAccount, ...rest } = params;
    const url = `${apiBaseUrl}/exams`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
        params: { ...rest, account_id: selectedAccount },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch exams");
    }
  }
);

export const updateFactor = createAsyncThunk(
  "exams/updateFactor",
  async (
    { factorId, able, work_restrictions, examiner_comments, selectedAccount },
    { rejectWithValue }
  ) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/functions/update_factor/${factorId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.patch(
        url,
        { able, work_restrictions, examiner_comments },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return { factorId, able, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating factor");
    }
  }
);

export const createJob = createAsyncThunk(
  "exams/createJob",
  async ({ function: jobFunction, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/functions?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(
        url,
        { function: jobFunction },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to create job");
    }
  }
);

export const updateJob = createAsyncThunk(
  "exams/updateJob",
  async ({ function: jobFunction, id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/functions/${id}?account_id=${selectedAccount}`;
    try {
      const response = await axios.patch(
        url,
        { function: jobFunction },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update job");
    }
  }
);

// The exam's job as WorkerFIT has it now. `previewJobRefresh` only reports what
// would change; `applyJobRefresh` makes the change, keeping the examiner's work.
export const previewJobRefresh = createAsyncThunk(
  "exams/previewJobRefresh",
  async ({ exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/job_refresh?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to check WorkerFIT for changes");
    }
  }
);

export const applyJobRefresh = createAsyncThunk(
  "exams/applyJobRefresh",
  async ({ exam_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${exam_id}/job_refresh?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(url, null, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update the job from WorkerFIT");
    }
  }
);

export const examJobs = createAsyncThunk(
  "exams/examJobs",
  async ({ subject_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/functions/exam_jobs?subject_id=${subject_id}&account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data.exam_jobs; // Extract exam_jobs from response
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch exam jobs");
    }
  }
);

export const archiveExam = createAsyncThunk(
  "exams/archiveExam",
  async ({ examId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${examId}/archive?account_id=${selectedAccount}`;

    try {
      await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Exam archived successfully");
      return { examId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to archive" });
    }
  }
);

export const unarchiveExam = createAsyncThunk(
  "exams/unarchiveExam",
  async ({ examId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${examId}/unarchive?account_id=${selectedAccount}`;

    try {
      await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Exam unarchived successfully");
      return { examId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to unarchive" });
    }
  }
);

export const checkReportStatus = createAsyncThunk(
  "exams/checkReportStatus",
  async ({ examId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exams/${examId}/check_report_status?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data; // Expected response: { report_job_completed: true/false }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to check report status");
    }
  }
);

export const createHemscapSession = createAsyncThunk(
  "exams/createHemscapSession",
  async ({ exam_id, exam_test_id, exercise_key, exercise_type, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/hemscap_sessions?account_id=${selectedAccount}`;

    try {
      const response = await axios.post(
        url,
        { exam_id, exam_test_id, exercise_key, exercise_type },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (typeof data === "string" && data.trim()) {
        return rejectWithValue({ status: "error", message: data.trim(), httpStatus: status });
      }
      if (data && typeof data === "object") {
        return rejectWithValue({ ...data, httpStatus: status });
      }
      return rejectWithValue({
        status: "error",
        message:
          error.message?.trim() ||
          "Failed to create motion capture session (no response from server).",
        httpStatus: status,
      });
    }
  }
);

export const getHemscapSessionStatus = createAsyncThunk(
  "exams/getHemscapSessionStatus",
  async ({ sessionId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/hemscap_sessions/${sessionId}?account_id=${selectedAccount}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (typeof data === "string" && data.trim()) {
        return rejectWithValue({ status: "error", message: data.trim(), httpStatus: status });
      }
      if (data && typeof data === "object") {
        return rejectWithValue({ ...data, httpStatus: status });
      }
      return rejectWithValue({
        status: "error",
        message:
          error.message?.trim() ||
          "Failed to fetch motion capture session (no response from server).",
        httpStatus: status,
      });
    }
  }
);

export const getHemscapExercise = createAsyncThunk(
  "exams/getHemscapExercise",
  async ({ exerciseKey, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/hemscap/exercise?exercise_key=${encodeURIComponent(exerciseKey)}&account_id=${selectedAccount}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (typeof data === "string" && data.trim()) {
        return rejectWithValue({ status: "error", message: data.trim(), httpStatus: status });
      }
      if (data && typeof data === "object") {
        return rejectWithValue({ ...data, httpStatus: status });
      }
      return rejectWithValue({
        status: "error",
        message: error.message?.trim() || "Failed to fetch motion capture exercise.",
        httpStatus: status,
      });
    }
  }
);

export const syncExamTestFromHemscap = createAsyncThunk(
  "exams/syncExamTestFromHemscap",
  async ({ exam_test_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exam_tests/${exam_test_id}/sync_from_hemscap?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (data && typeof data === "object") {
        return rejectWithValue({ ...data, httpStatus: status });
      }
      return rejectWithValue({
        status: "error",
        message: error.message?.trim() || "Failed to sync exam test.",
        httpStatus: status,
      });
    }
  }
);

// Manual recovery: re-applies the latest completed HemsCap session's
// already-computed field_prefill straight to this test's saved fields,
// server-side — for when the live poll (HemscapTest.jsx) never got the
// chance to catch it (reload, closed tab, navigated away before HemsCap
// replied). Caller should re-fetch exam details afterward to show the result.
export const applyHemscapResults = createAsyncThunk(
  "exams/applyHemscapResults",
  async ({ exam_test_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exam_tests/${exam_test_id}/apply_hemscap_results?account_id=${selectedAccount}`;
    try {
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (data && typeof data === "object") {
        return rejectWithValue({ ...data, httpStatus: status });
      }
      return rejectWithValue({
        status: "error",
        message: error.message?.trim() || "Failed to apply motion capture results.",
        httpStatus: status,
      });
    }
  }
);

export const updateExamTestSaved = createAsyncThunk(
  "exams/updateExamTestSaved",
  async ({ examTestId, savedValue, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/exam_tests/${examTestId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.patch(
        url,
        { saved: savedValue },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating exam test");
    }
  }
);
export const getComments = createAsyncThunk(
  "exams/getComments",
  async (examId, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const { selectedAccount } = getState().user;
    const url = `${apiBaseUrl}/comments?exam_id=${examId}&account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to fetch comments");
      return rejectWithValue(error.response?.data?.error || "Failed to fetch comments");
    }
  }
);

// Add to your existing API file (e.g., exam.js)
export const updateComment = createAsyncThunk(
  "exams/updateComment",
  async ({ commentId, resolved }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const { selectedAccount } = getState().user;
    const url = `${apiBaseUrl}/comments/${commentId}?account_id=${selectedAccount}`;
    try {
      const response = await axios.put(
        url,
        { comment: { resolved } },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      toast.success(`Comment marked as ${resolved ? "resolved" : "unresolved"}`);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.errors?.join(", ") || "Failed to update comment");
      return rejectWithValue(error.response?.data?.errors || "Failed to update comment");
    }
  }
);
export const createComment = createAsyncThunk(
  "exams/createComment",
  async ({ examId, content }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const { selectedAccount } = getState().user;
    const url = `${apiBaseUrl}/comments?exam_id=${examId}&account_id=${selectedAccount}`;
    try {
      const response = await axios.post(
        url,
        { comment: { content, exam_id: examId } },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      toast.success("Comment added successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.errors?.join(", ") || "Failed to add comment");
      return rejectWithValue(error.response?.data?.errors || "Failed to add comment");
    }
  }
);

export const deleteComment = createAsyncThunk(
  "exams/deleteComment",
  async ({ commentId }, { rejectWithValue, getState }) => {
    const bearerToken = getToken();
    const { selectedAccount } = getState().user;
    const url = `${apiBaseUrl}/comments/${commentId}?account_id=${selectedAccount}`;
    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("Comment deleted successfully");
      return commentId;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to delete comment");
      return rejectWithValue(error.response?.data?.error || "Failed to delete comment");
    }
  }
);

export const examSlice = createSlice({
  name: "exam",
  initialState,
  reducers: {
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    addJobToSelection: (state, action) => {
      state.selectedJobs = [action.payload];
      state.examJobs = [];
    },
    removeJobFromSelection: (state, action) => {
      const jobId = action.payload;
      state.selectedJobs = state.selectedJobs.filter((j) => j.id !== jobId);
      state.examJobs = [];
    },
    clearSelectedJobs: (state) => {
      state.selectedJobs = [];
      state.examJobs = [];
    },
    deselectJob: (state) => {
      state.selectedJobs = [];
      state.examJobs = [];
    },
    clearJobs: (state) => {
      state.jobs = [];
      state.examJobs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConsultantCompanies.pending, (state) => {
        state.consultantCompaniesLoading = true;
        state.consultantCompaniesError = null;
      })
      .addCase(getConsultantCompanies.fulfilled, (state, action) => {
        state.consultantCompanies = action.payload;
        state.consultantCompaniesLoading = false;
      })
      .addCase(getConsultantCompanies.rejected, (state, action) => {
        state.consultantCompaniesLoading = false;
        state.consultantCompaniesError =
          action.payload || "Failed to fetch consultant companies";
      })
      .addCase(getWorkerExams.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWorkerExams.fulfilled, (state, action) => {
        state.workerExamsList = action.payload;
        state.isLoading = false;
      })
      .addCase(getWorkerExams.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAllExams.pending, (state, action) => {
        const disableLoading = action.meta.arg?.disableLoading;
        state.isLoading = !disableLoading;
        state.currentExam = null;
      })
      .addCase(getAllExams.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
      })
      .addCase(getAllExams.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteExam.fulfilled, (state, action) => {
        state.data.exams = state.data.exams.filter(
          (exam) => exam.id !== action.payload.exam_id
        );
        state.workerExamsList = state.workerExamsList.filter(
          (exam) => exam.id !== action.payload.exam_id
        );
      })
      .addCase(getExam.pending, (state, action) => {
        const showLoading = action.meta.arg?.showLoading;
        if (showLoading !== false) {
          state.isLoading = true;
        }
      })
      .addCase(getExam.fulfilled, (state, action) => {
        state.currentExam = action.payload;
        state.isLoading = false;
      })
      // No isLoading here, unlike getExam: the report is embedded in the exam
      // page, which swaps itself for a spinner while isLoading is true — that
      // would unmount the viewer that dispatched this and loop. The viewer
      // tracks its own loading state.
      .addCase(getExamReportData.fulfilled, (state, action) => {
        state.currentExam = action.payload;
      })
      .addCase(getExam.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchExamSummary.pending, (state) => {
        state.functionReportLoading = true;
        state.functionReportError = null;
        state.functionReport = null;
      })
      .addCase(fetchExamSummary.fulfilled, (state, action) => {
        state.functionReportLoading = false;
        state.functionReport = action.payload;
      })
      .addCase(fetchExamSummary.rejected, (state, action) => {
        state.functionReportLoading = false;
        state.functionReportError = action.payload;
      })
      .addCase(getExamDetails.pending, (state, action) => {
        if (action.meta.arg.showLoading) {
          state.isLoading = true;
        }
      })
      .addCase(copyExam.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(copyExam.fulfilled, (state, action) => {
        state.data.exams = [...state.data.exams, action.payload];
        state.currentExam = action.payload;
        state.isLoading = false;
      })
      .addCase(copyExam.rejected, (state, action) => {
        state.isLoading = false;
        toast.error(action.payload || "Failed to copy exam");
      })
      .addCase(examJobs.pending, (state) => {
        state.examJobsLoading = true;
        state.examJobsError = null;
      })
      .addCase(examJobs.fulfilled, (state, action) => {
        state.examJobs = action.payload;
        state.examJobsLoading = false;
      })
      .addCase(examJobs.rejected, (state, action) => {
        state.examJobs = [];
        state.examJobsLoading = false;
        state.examJobsError = action.payload || "Failed to fetch exam jobs";
      })
      .addCase(shareExamSummary.pending, (state) => {
        state.isLoading = true;
        state.shareSummaryError = null;
      })
      .addCase(shareExamSummary.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(shareExamSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.shareSummaryError = action.payload || "Failed to share exam summary";
      })
      .addCase(getExamDetails.fulfilled, (state, action) => {
        state.examDetails = action.payload;
        state.isLoading = false;
      })
      .addCase(getExamDetails.rejected, (state) => {
        state.isLoading = false;
      })
      // Set when the delete is sent, not when it returns: the report tab can be
      // clicked in between, and the stored report still has the test in it.
      .addCase(deleteExamTest.pending, (state) => {
        state.reportDirty = true;
      })
      .addCase(fillExam.pending, (state) => {
        state.isLoading = false;
        // A test save is now in flight — the Report Preview tab shows the
        // "data is getting saved" notice while this is true.
        state.saving = true;
      })
      .addCase(fillExam.fulfilled, (state, action) => {
        // Do NOT overwrite examDetails with the fill_exam response: that endpoint
        // returns a confirmation string, not an exam object, which wiped out
        // examDetails.id/job_run and made the Report Preview tab show "data is
        // saving" until the exam status was changed. The form keeps its own
        // up-to-date copy (examDetailsData) via the save callbacks.
        state.isLoading = false;
        state.saving = false;
        // The saved data isn't in the report yet → preview must regenerate.
        state.reportDirty = true;
      })
      .addCase(fillExam.rejected, (state) => {
        state.isLoading = false;
        state.saving = false;
      })
      .addCase(updateExamTestSaved.pending, (state) => {
        state.isLoading = false;
      })
      .addCase(updateExamTestSaved.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateExamTestSaved.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createNewExam.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createNewExam.fulfilled, (state, action) => {
        state.currentExam = action.payload;
        state.isLoading = false;
        state.selectedJobs = [];
      })
      .addCase(createNewExam.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(archiveExam.fulfilled, (state, action) => {
        state.data.exams = state.data.exams.filter(e => e.id !== action.payload.examId);
      })
      .addCase(unarchiveExam.fulfilled, (state, action) => {
        state.data.exams = state.data.exams.filter(e => e.id !== action.payload.examId);
      })
      .addCase(editExam.pending, (state, action) => {
        if (action.meta.arg.showLoading) {
          state.isLoading = true;
        }
      })
      .addCase(editExam.fulfilled, (state, action) => {
        state.currentExam = action.payload;
        state.examDetails = action.payload;
        state.isLoading = false;
        // editExam triggers the report regeneration, so the report is no
        // longer "dirty" relative to the last save.
        state.reportDirty = false;
      })
      .addCase(editExam.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getCompanies.pending, (state) => {
        state.companiesLoading = true;
        state.companiesError = null;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.companies = action.payload;
        state.companiesLoading = false;
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.companiesLoading = false;
        state.companiesError = action.payload || "Failed to fetch companies";
      })
      .addCase(fetchCompanyJobs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCompanyJobs.fulfilled, (state, action) => {
        const newJobs = action.payload.filter(
          (newJob) =>
            !state.jobs.some((existingJob) => existingJob.id === newJob.id)
        );
        state.jobs = [...state.jobs, ...newJobs];
        state.isLoading = false;
      })
      .addCase(fetchCompanyJobs.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getComments.pending, (state) => {
        state.commentsLoading = true;
        state.commentsError = null;
      })
      .addCase(getComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments = action.payload; // { comments: [], exam_comments: string }
      })
      .addCase(getComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.commentsError = action.payload;
      })
      .addCase(updateComment.pending, (state) => {
        state.commentsLoading = true;
        state.commentsError = null;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments.comments = state.comments.comments.map((comment) =>
          comment.id === action.payload.id ? action.payload : comment
        );
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.commentsLoading = false;
        state.commentsError = action.payload;
      })
      .addCase(createComment.pending, (state) => {
        state.commentsLoading = true;
        state.commentsError = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments.comments = [action.payload, ...state.comments.comments]; // Append new comment
      })
      .addCase(createComment.rejected, (state, action) => {
        state.commentsLoading = false;
        state.commentsError = action.payload;
      })
      .addCase(deleteComment.pending, (state) => {
        state.commentsLoading = true;
        state.commentsError = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments.comments = state.comments.comments.filter(
          (comment) => comment.id !== action.payload
        );
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.commentsLoading = false;
        state.commentsError = action.payload;
      })
      .addCase(createJob.pending, (state) => {
        state.isLoading = false;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(createJob.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateJob.pending, (state) => { })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateJob.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(workerJobs.pending, (state) => {
        state.workerJobsLoading = true;
      })
      .addCase(workerJobs.fulfilled, (state, action) => {
        state.workerJobs = action.payload;
        state.workerJobsLoading = false;
      })
      .addCase(workerJobs.rejected, (state) => {
        state.workerJobs = [];
        state.workerJobsLoading = false;
      })
      .addCase(fetchCompanyJobsForEdit.pending, (state) => { })
      .addCase(fetchCompanyJobsForEdit.fulfilled, (state, action) => {
        const newJobs =
          action.payload && Array.isArray(action.payload)
            ? action.payload.filter(
              (newJob) =>
                !state.jobs.some((existingJob) => existingJob.id === newJob.id)
            )
            : [];
        state.jobs = [...state.jobs, ...newJobs];
      })
      .addCase(fetchFunctionReport.pending, (state) => {
        state.functionReportLoading = true;
        state.functionReportError = null;
        state.functionReport = null;
      })
      .addCase(fetchFunctionReport.fulfilled, (state, action) => {
        state.functionReport = action.payload;
        state.functionReportLoading = false;
      })
      .addCase(fetchFunctionReport.rejected, (state, action) => {
        state.functionReportLoading = false;
        state.functionReportError = action.payload;
      })
      .addCase(checkReportStatus.pending, (state) => {
        state.reportJobLoading = true;
        state.reportJobError = null;
      })
      .addCase(checkReportStatus.fulfilled, (state, action) => {
        state.reportJobLoading = false;
      })
      .addCase(checkReportStatus.rejected, (state, action) => {
        state.reportJobLoading = false;
        state.reportJobError = action.payload || "Failed to check report status";
      })
      .addCase(createHemscapSession.pending, () => {})
      .addCase(createHemscapSession.fulfilled, () => {})
      .addCase(createHemscapSession.rejected, () => {})
      .addCase(getHemscapSessionStatus.pending, (state) => {
        state.reportJobLoading = true;
      })
      .addCase(getHemscapSessionStatus.fulfilled, (state) => {
        state.reportJobLoading = false;
      })
      .addCase(getHemscapSessionStatus.rejected, (state, action) => {
        state.reportJobLoading = false;
        state.reportJobError = action.payload || "Failed to fetch motion capture status";
      })
      .addCase(syncExamTestFromHemscap.pending, () => {})
      .addCase(syncExamTestFromHemscap.fulfilled, () => {})
      .addCase(syncExamTestFromHemscap.rejected, () => {})
      .addCase(applyHemscapResults.pending, () => {})
      .addCase(applyHemscapResults.fulfilled, () => {})
      .addCase(applyHemscapResults.rejected, () => {})
      .addCase(updateFactor.pending, (state) => { })
      .addCase(updateFactor.fulfilled, (state, action) => {
        const { factorId, able } = action.payload;
        state.workerJobs = state.workerJobs.map((job) => ({
          ...job,
          function_factors_by_category: Object.fromEntries(
            Object.entries(job.function_factors_by_category).map(
              ([category, factors]) => [
                category,
                factors.map((factor) =>
                  factor.id === factorId ? { ...factor, able } : factor
                ),
              ]
            )
          ),
        }));
        state.isLoading = false;
      })
      .addCase(updateFactor.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteWorkerJob.fulfilled, (state, action) => {
        state.workerJobs = state.workerJobs.filter(
          (job) => job.id !== action.payload.jobId
        );
      })
      .addCase(deleteWorkerJob.rejected, (state, action) => {
        toast.error(action.payload || "Failed to delete worker job");
      });
  },
});

export const {
  setSelectedTab,
  addJobToSelection,
  removeJobFromSelection,
  clearSelectedJobs,
  deselectJob,
  clearJobs,
} = examSlice.actions;

export default examSlice.reducer;
